import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Users, BookOpen, TrendingUp, MessageSquare, UserCheck } from "lucide-react";
import { toast } from "sonner";

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    
    // Set up real-time updates for unread messages - more frequent updates
    const interval = setInterval(() => {
      fetchUnreadMessagesCount();
    }, 15000); // Update every 15 seconds instead of 30

    // Update when user comes back to the tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUnreadMessagesCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Update when user focuses on window
    const handleFocus = () => {
      fetchUnreadMessagesCount();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      // Use a dedicated endpoint for just the unread count to be more efficient
      const response = await axios.get(`${API}/dashboard/unread-count`);
      setStats(prevStats => ({
        ...prevStats,
        unread_messages: response.data.unread_messages
      }));
    } catch (error) {
      console.error("Failed to update unread messages count");
    }
  };

  const handleCardClick = (path) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  const adminCards = [
    {
      title: "Total Students",
      titleSo: "Wadarta Ardayda",
      value: stats?.total_students || 0,
      icon: Users,
      color: "bg-blue-500",
      path: "/students"
    },
    {
      title: "Total Teachers",
      titleSo: "Wadarta Macalimiinta",
      value: stats?.total_teachers || 0,
      icon: UserCheck,
      color: "bg-teal-500",
      path: "/teachers"
    },
    {
      title: "Total Classes",
      titleSo: "Wadarta Fasallada",
      value: stats?.total_classes || 0,
      icon: BookOpen,
      color: "bg-green-500",
      path: "/classes"
    },
    {
      title: "Attendance Rate",
      titleSo: "Heerka Soo-gooshashada",
      value: `${stats?.attendance_percentage || 0}%`,
      icon: TrendingUp,
      color: "bg-purple-500",
      path: "/attendance"
    },
    {
      title: "Fee Collection",
      titleSo: "Ururinta Lacagaha",
      value: `${stats?.fee_collection_percentage || 0}%`,
      icon: TrendingUp,
      color: "bg-[#E4C987]",
      path: "/finance"
    },
    {
      title: "Unread Messages",
      titleSo: "Farriimaha Aan La Akhrin",
      value: stats?.unread_messages || 0,
      icon: MessageSquare,
      color: "bg-red-500",
      path: "/messages"
    },
  ];

  const teacherCards = [
    {
      title: "My Classes",
      titleSo: "Fasalladayda",
      value: stats?.my_classes || 0,
      icon: BookOpen,
      color: "bg-green-500",
      path: "/classes"
    },
    {
      title: "My Students",
      titleSo: "Ardaydayda",
      value: stats?.my_students || 0,
      icon: Users,
      color: "bg-blue-500",
      // No path - this card is non-clickable for teachers
    },
    {
      title: "Unread Messages",
      titleSo: "Farriimaha Aan La Akhrin",
      value: stats?.unread_messages || 0,
      icon: MessageSquare,
      color: "bg-red-500",
      path: "/messages"
    },
  ];

  const cards = (user.role === "admin" || user.role === "super_admin") ? adminCards : teacherCards;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome message */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold text-[#1E624A] mb-2">
          Welcome back, {user.name}!
        </h2>
        <p className="text-gray-600">
          {(user.role === "admin" || user.role === "super_admin")
            ? "Here's an overview of your school's performance."
            : "Here's an overview of your classes and students."}
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          const isClickable = !!card.path; // Card is clickable only if it has a path
          
          return (
            <div
              key={index}
              data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              className={`bg-white rounded-xl p-6 shadow-md ${
                isClickable ? 'dashboard-card-clickable cursor-pointer' : 'cursor-default'
              }`}
              onClick={isClickable ? () => handleCardClick(card.path) : undefined}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={isClickable ? `Navigate to ${card.title}` : undefined}
              onKeyDown={isClickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardClick(card.path);
                }
              } : undefined}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-600">{card.title}</p>
                  <p className="text-xs text-gray-400 mt-1">{card.titleSo}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-3">
                    {card.value}
                  </p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon size={24} className="text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Shortcuts - Minimized */}
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="flex items-center justify-between">
          <h3 className="text-md font-semibold text-[#1E624A]">
            Shortcuts / Goobo Dhaqso leh
          </h3>
          <div className="flex gap-2">
            {user.role === "admin" ? (
              <>
                <button
                  onClick={() => navigate("/students")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-students"
                >
                  👥 Students
                </button>
                <button
                  onClick={() => navigate("/attendance")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-attendance"
                >
                  📋 Attendance
                </button>
                <button
                  onClick={() => navigate("/finance")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-finance"
                >
                  💰 Finance
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/attendance")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-attendance"
                >
                  📋 Attendance
                </button>
                <button
                  onClick={() => navigate("/assessments")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-assessments"
                >
                  📊 Grades
                </button>
                <button
                  onClick={() => navigate("/classes")}
                  className="px-3 py-2 text-xs border border-[#1E624A] rounded-lg hover:bg-[#1E624A] hover:text-white transition-all font-medium"
                  data-testid="shortcut-classes"
                >
                  📚 Classes
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
